<script setup lang="ts">
import { enumToKeys, formatNumber, renderIcon, toTitleCase } from '@/infrastructure/helpers';
import type { ManualTurnover } from '@/infrastructure/types';
import { api, useFetch, useUpdate } from '@/plugins/api';
import { ArrowClockwise20Regular, ArrowDownload24Regular, ArrowEnterLeft20Regular, Dismiss20Regular, Filter20Regular, Save20Regular } from '@vicons/fluent';
import { isAxiosError, type AxiosResponse } from 'axios';
import { NFlex, NInputNumber, NTag, type DataTableColumn, NCheckbox } from 'naive-ui';
import { h, onMounted, reactive, ref, computed } from 'vue'
import { debounce } from 'lodash'
import { ManualTurnoverType } from '@/infrastructure/enums';

document.addEventListener('keyup', enterKeyHandler)

type Query = {
  hasResponse?: boolean | null
  textSearch?: string
  periods?: string[] | null
  periodKeys?: string[]
  isIntegrated?: boolean
  types?: ManualTurnoverType[]
}

const basePath = 'manual-turnovers'
const notification = useNotification()

const items = ref<ManualTurnover[]>([])
const showFilters = ref(false)
const loading = ref(true)
const exporting = ref(false)
const periodKeys = await useFetch(basePath + '/period-keys')
const updating = ref(false)
const query = ref<Query>({
  hasResponse: false
})
const pagination = reactive({
  page: 1,
  pageSize: 25,
  pageSizes: [10, 25, 50],
  showSizePicker: true,
  onChange: (page: number) => {
    pagination.page = page
  },
  onUpdatePageSize: (pageSize: number) => {
    pagination.pageSize = pageSize
    pagination.page = 1
  }
})
const updatedItems = computed(() => items.value?.filter(item => typeof item.newTurnover === 'number' && typeof item.newTransactions === 'number' && typeof item.newQuantity === 'number' ))

const columns: DataTableColumn<ManualTurnover>[] = [
  { title: 'Operator', key: 'groupName' },
  { title: 'Shop', key: 'shopNameWithLocation' },
  { title: 'Type', key: 'type', render: (row) => toTitleCase(row.type.toString()) },
  { title: 'Contract', key: 'contractNumber', width: 110 },
  { title: 'Period type', key: 'period', width: 100 },
  { title: 'Period key', key: 'periodKey', width: 100 },
  { title: 'Rule', key: 'rule', render: (row) => `${row.ruleNumber}${row.ruleDescription ? ' - ' + row.ruleDescription : ''}` },
  { title: 'Values', key: 'values', align: 'right', children: [
    { title: 'Turnover', key: 'turnover', align: 'right', width: 220, render: (row) => {
      if (row.reportedTurnover) {
        return h(NFlex, null, () => [
          row.isAdjusted ? h(NTag, { type: 'info', size: 'small' }, { default: () => 'Adjusted' }) : null,
          h('div', { class: 'text-right ml-auto', innerHTML: formatNumber(row.reportedTurnover, 0) })
        ])
      } else {
        return h(NInputNumber, { size: 'small', showButton: false, value: row.newTurnover, onUpdateValue:(val) => row.newTurnover = val as number })
      }
    }},
    { title: 'Transactions', key: 'transactions', align: 'right', width: 220, render: (row) => {
      if (row.reportedTransactions) {
        return h('div', { class: 'text-right', innerHTML: formatNumber(row.reportedTransactions, 0) })
      } else {
        return h(NInputNumber, { size: 'small', showButton: false, value: row.newTransactions, onUpdateValue:(val) => row.newTransactions = val as number })
      }
    }},
    { title: 'Quantity', key: 'quantity', align: 'right', width: 220, render: (row) => {
      if (row.reportedQuantity) {
        return h('div', { class: 'text-right', innerHTML: formatNumber(row.reportedQuantity, 0) })
      } else {
        return h(NInputNumber, { size: 'small', showButton: false, value: row.newQuantity, onUpdateValue:(val) => row.newQuantity = val as number })
      }
    }}
  ]},
]

async function getData() {
  loading.value = true
  items.value = []

  try {
    // Transform periodTypes
    const data = await useFetch(basePath, query.value)
    items.value = data
  } finally {
    loading.value = false
  }
}

async function exportToFile() {
  exporting.value = true

  const filename = `aggregated_${new Date().toLocaleDateString()}.xlsx`

  try {
    const { data } = await api.get('manual-turnovers/export', {
      params: query.value,
      paramsSerializer: {
        indexes: null
      },
      responseType: 'blob'
    })
  
    const href = URL.createObjectURL(data)
    const link = document.createElement('a')
    link.href = href
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
  
    // Clean up
    document.body.removeChild(link)
    URL.revokeObjectURL(href)
  }
  catch (error) {
    notification.error({
      content: 'Error encountered when exporting sales'
    })
    console.error(error)
  }
  finally {
    exporting.value = false
  }
}

async function saveAll() {
  updating.value = true
  let completed: string[] = []
  let errors: {
    response: AxiosResponse,
    item: ManualTurnover
  }[] = []

  for (const item of updatedItems.value) {
    const { newTurnover, newTransactions, newQuantity } = item

    try {
      await useUpdate(basePath + '/' + item.id, { newTurnover, newTransactions, newQuantity }, { showFeedback: false })
      completed.push(item.shopNameWithLocation + ' - ' + item.periodKey)
    } catch (error: unknown) {
      if (isAxiosError(error) && error.response) {
        errors.push({ response: error.response, item })
      }
    }
  }

  if (completed.length > 0) {
    notification.success({
      title: `${completed.length} ${completed.length === 1 ? 'item' : 'items'} saved`,
      content: () => h(NFlex, { vertical: true, size: 'small' }, () => completed.map(x => h('div', x))),
      duration: 3000
    })
  }

  if (errors.length > 0) {
    for (const error of errors) {
      notification.error({
        title: error.response.data.title,
        description: error.item.shopNameWithLocation + ' - ' + error.item.periodKey,
        content: (error.response.data.detail ?? 'Unknown error') + '.',
        duration: 10000
      })
    }
  }

  updating.value = false
  await getData()

  // Update values the user put in
  errors.forEach((error) => {
    const index =  items.value.findIndex((item: ManualTurnover) => item.id === error.item.id)
    items.value[index].newTurnover = error.item.newTurnover
    items.value[index].newTransactions = error.item.newTransactions
    items.value[index].newQuantity = error.item.newQuantity
  })
}

function enterKeyHandler(ev: KeyboardEvent) {
  if (ev.ctrlKey && ev.key === 'Enter') {
    if (updatedItems.value.length) saveAll()
  }
}

watch(query, debounce(() => getData(), 300), { deep: true })

onMounted(() => {
  getData()
})

onBeforeUnmount(() => {
  removeEventListener('keyup', enterKeyHandler)
})
</script>

<template>
  <cdi-toolbar title="Aggregated reporting">
    <template #subtitle>
      <div class="flex items-center gap-1">
        <div>Ctrl</div>
        <div>+</div>
        <n-icon :component="ArrowEnterLeft20Regular"></n-icon> to save changes.
      </div>
    </template>
    <template #extra>
      <div class="flex gap-3 items-center">
        <n-input
          v-model:value="query.textSearch"
          placeholder="Search reports"
          clearable
          :style="{ width: '400px' }"
          v-focus
        />
        <n-button :render-icon="() => h(Filter20Regular)" @click="showFilters = !showFilters"></n-button>
        <n-button :render-icon="() => h(ArrowClockwise20Regular)" @click="getData">Refresh</n-button>
        <n-popconfirm
          positive-text="Yes, export"
          :on-positive-click="exportToFile"
        >
          <template #trigger>
            <n-button
              :loading="exporting"
              :render-icon="renderIcon(ArrowDownload24Regular)"
            >
              Export
            </n-button>
          </template>
          This will export the currently visible reports. Do you want to continue?
        </n-popconfirm>
        <n-button :render-icon="() => h(Save20Regular)" :disabled="!updatedItems.length" @click="saveAll"
          :loading="updating">
          Save all
        </n-button>
      </div>
    </template>
  </cdi-toolbar>

  <n-collapse-transition :show="showFilters">
    <n-card>
      <n-form label-placement="left" label-width="auto" label-align="left">
        <n-form-item label="Period type">
          <n-radio-group v-model:value="query.periods">
            <n-radio-button value="monthly" label="Monthly"></n-radio-button>
            <n-radio-button value="weekly" label="Weekly"></n-radio-button>
          </n-radio-group>
          <n-button class="!ml-2" @click="query.periods = null" circle size="tiny"
            :render-icon="() => h(Dismiss20Regular)"></n-button>
        </n-form-item>
        <n-form-item label="Types">
          <n-select v-model:value="query.types" filterable multiple :options="enumToKeys(ManualTurnoverType).map((x: string) => ({ label: toTitleCase(x), value: x }))"></n-select>
        </n-form-item>
        <n-form-item label="Period">
          <n-select v-model:value="query.periodKeys" filterable multiple :options="periodKeys.map((x: string) => ({ label: x, value: x }))"></n-select>
        </n-form-item>
        <n-form-item label="Response status" :show-feedback="false">
          <n-radio-group v-model:value="query.hasResponse">
            <n-radio-button :value="false" label="Only without response"></n-radio-button>
            <n-radio-button :value="true" label="Only responded"></n-radio-button>
            <n-radio-button :value="null" label="Show all"></n-radio-button>
          </n-radio-group>
        </n-form-item>
      </n-form>
    </n-card>
  </n-collapse-transition>

  <n-data-table :columns="columns" :data="items" :loading="loading" :pagination="pagination" />
</template>